/*
 * Copyright © 2017-2021 Skale Education Inc. - All Rights Reserved
 */

import { useEffect, useState } from "react";
import { View } from "react-native";
import Rodal from "rodal";

import { useSelector } from "react-redux";
import { RootState, SelfObject } from "store/types";

import { Button, Checkbox, Portal, Text } from "UpduoLibrary";
import { skaleModalTextGray, upduoPrimaryBlack } from "config/UpduoColors";
import { FilterSet } from "../types";

interface FilterModalProps {
  isVisible: boolean;
  initialFilterSet: FilterSet;
  onClose: () => void;
  onApply: (filters: FilterSet) => void;
}

export default function FilterModal({
  isVisible,
  onClose,
  onApply,
  initialFilterSet,
}: FilterModalProps) {
  const selfObject: SelfObject = useSelector(
    (state: RootState) => state.selfObject
  );

  const [filters, setFilters] = useState<FilterSet>(initialFilterSet);
  useEffect(() => {
    setFilters(initialFilterSet);
  }, [initialFilterSet]);

  const setOrClearFilter = (filter: keyof FilterSet, value: boolean) => {
    setFilters((old) => ({
      ...old,
      [filter]: old[filter] === value ? undefined : value,
    }));
  };

  const applyFilterBtnText = (
    <>
      <Text
        style={{
          color: "#367CF6",
          fontStyle: "normal",
          fontWeight: "500",
          fontSize: 16,
        }}
      >
        {"Apply"}
      </Text>
    </>
  );

  const clearFilterBtnText = (
    <>
      <Text
        style={{
          color: upduoPrimaryBlack,
          fontStyle: "normal",
          fontWeight: "400",
          fontSize: 16,
        }}
      >
        {"Clear"}
      </Text>
    </>
  );

  return (
    <Portal>
      <Rodal
        visible={isVisible}
        showMask={true}
        onClose={onClose}
        closeOnMaskClick={true}
        animation={"fade"}
        customMaskStyles={{ opacity: 0 }}
        customStyles={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          width: 314,
          height: 350,
          position: "absolute",
          padding: 0,
          borderRadius: 8,
          shadowColor: "rgba(0, 0, 0, 0.12)",
        }}
      >
        <View
          style={{
            display: "flex",
            flexDirection: "column",
            width: 254,
            height: 260,
          }}
        >
          <Text
            isHeading={true}
            style={{
              color: upduoPrimaryBlack,
              fontSize: 18,
              fontStyle: "normal",
              fontWeight: "700",
            }}
          >
            {"Filters"}
          </Text>

          <View style={{ height: 50, marginTop: 10 }}>
            <Text
              isHeading={true}
              style={{
                color: upduoPrimaryBlack,
                fontSize: 14,
                fontStyle: "normal",
                fontWeight: "700",
              }}
            >
              {"Session Status"}
            </Text>
            <View
              style={{
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "space-between",
                width: 254,
                height: 21,
              }}
            >
              <CheckboxElement
                text={"Complete"}
                checked={filters.complete === true}
                onClick={() => setOrClearFilter("complete", true)}
              />
              <CheckboxElement
                text={"Incomplete"}
                checked={filters.complete === false}
                onClick={() => setOrClearFilter("complete", false)}
              />
            </View>
          </View>

          <View style={{ height: 50, marginTop: 21 }}>
            <Text
              isHeading={true}
              style={{
                color: upduoPrimaryBlack,
                fontSize: 14,
                fontStyle: "normal",
                fontWeight: "700",
              }}
            >
              {"Video Recorded"}
            </Text>
            <View
              style={{
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "space-between",
                width: 254,
                height: 21,
              }}
            >
              <CheckboxElement
                text={"With video"}
                checked={filters.withVideo === true}
                onClick={() => setOrClearFilter("withVideo", true)}
              />
              <CheckboxElement
                text={"Without video"}
                checked={filters.withVideo === false}
                onClick={() => setOrClearFilter("withVideo", false)}
              />
            </View>
          </View>

          {selfObject.organizationShowTranscription ? (
            <View style={{ height: 81, marginTop: 21 }}>
              <Text
                isHeading={true}
                style={{
                  color: upduoPrimaryBlack,
                  fontSize: 14,
                  fontStyle: "normal",
                  fontWeight: "700",
                }}
              >
                {"Transcript"}
              </Text>
              <View
                style={{
                  flexDirection: "column",
                  width: 254,
                  height: 21,
                }}
              >
                <CheckboxElement
                  text={"With transcript"}
                  checked={filters.withTranscript === true}
                  onClick={() => setOrClearFilter("withTranscript", true)}
                />
                <CheckboxElement
                  text={"Without transcript"}
                  checked={filters.withTranscript === false}
                  onClick={() => setOrClearFilter("withTranscript", false)}
                />
              </View>
            </View>
          ) : null}
        </View>

        <View
          style={{
            flexDirection: "row",
            justifyContent: "space-between",
            alignItems: "flex-end",
            height: 30,
            width: 254,
            marginTop: 20,
          }}
        >
          <Button
            onPress={() => {
              setFilters({});
            }}
            content={clearFilterBtnText}
            color={"white"}
            height={30}
            width={90}
            borderRadius={4}
            borderWidth={1}
            borderColor={skaleModalTextGray}
          />
          <Button
            onPress={() => onApply(filters)}
            content={applyFilterBtnText}
            color={"#E4F0F9"}
            height={30}
            width={150}
            borderRadius={4}
          />
        </View>
      </Rodal>
    </Portal>
  );
}

function CheckboxElement({
  text,
  checked,
  onClick,
}: {
  text: string;
  checked: boolean;
  onClick: (x: boolean) => void;
}) {
  return (
    <View
      style={{
        flexDirection: "row",
        alignItems: "center",
        marginLeft: -10,
      }}
    >
      <Checkbox checked={checked} onClick={onClick} />
      <Text
        style={{ color: upduoPrimaryBlack, fontSize: 16, fontWeight: "400" }}
      >
        {text}
      </Text>
    </View>
  );
}
